import { CloseOutlined } from "@ant-design/icons";
import { HEADER_LOGO, VINCENSE_ICON } from "@src/helpers/imagesConstants";
import { Button, Col, Modal, Row } from "antd";
import React, { useEffect, useState } from "react";
import { IoMdDownload } from "react-icons/io";
import moment from "moment";
import { getMyOrganization, getSubject, Reading } from "@src/redux/index";
import { useSelector } from "react-redux";
import { startCase } from "lodash";
import { getAgeCalculate } from "@src/helpers/constants";
import {
  convertEcgUrl,
  convertImageUrlToBase64,
  export_report_to_PDF,
  getBase64FromUrl,
  imageUrlToBase64 } from
"@src/helpers/exportPdf";
import { useTranslation } from "react-i18next";
import CustomTestDetails from "../../subject-details/subject-health-reading-details/CustomTest";
import axios from "axios";
import { Buffer } from "buffer";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import toast from "react-hot-toast";

// Configure PDF.js worker - use local worker file from react-pdf's pdfjs-dist (version 4.4.168)
if (typeof window !== "undefined") {
  // Use local worker file for better reliability (copied from react-pdf's pdfjs-dist)
  pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;
}

interface HealthScreeningReportModalProps {
  visible: boolean;
  onCancel: () => void;
  record: Reading;
}

const HealthScreeningReportModal: React.FC<HealthScreeningReportModalProps> = ({
  visible,
  onCancel,
  record
}) => {
  const { result: subject } = useSelector(getSubject);
  const { result: myOrgResult } = useSelector(getMyOrganization);
  const { t } = useTranslation();
  const [customTestList, setCustomTestList] = useState<any>();
  const [organizationImage, setOrganizationImage] = useState<any>();
  const [certificatePage, setCertificatePage] = useState<
    "reading" | "ecg" | "other_test">(
  );
  const [types, setTypes] = useState<string[]>(["reading"]);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | string | null>(null);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [ecgData, setEcgData] = useState<string | null>(null);

  const handleClick = async () => {
    try {
      // The stored logoUrl already points at the V3 logo bucket/CDN, and the
      // API resolves it against the configured V3 hosts. This used to rewrite
      // the URL onto the legacy vincense-logo-image / logo-image.vincense.com
      // pair, which aimed V3 at V1/V2 data.
      const orgLogoUrl = myOrgResult?.rootOrganization?.logoUrl || "";
      const orgName = myOrgResult?.rootOrganization?.name || "";
      const orgShortName =
      myOrgResult?.rootOrganization?.shortName || "-";
      const subjectType = record?.subject?.organization?.type || "";

      const base64ImageUrl = await getBase64FromUrl(orgLogoUrl);

      // Check if PDF URL exists, otherwise use ECG URL
      let ecgUrl = record?.ecgReading?.pdfUrl || record?.ecgReading?.ecgUrl || "";

      // Use ecgData if available (from PDF fetch), otherwise fetch it
      let ecgData = "";
      if (ecgUrl) {
        if (ecgUrl.endsWith('.pdf')) {
          // For PDF, always convert to image - use pdfBlob if available
          if (pdfBlob && pdfBlob instanceof Blob) {
            try {
              const arrayBuffer = await pdfBlob.arrayBuffer();
              const pdfData = new Uint8Array(arrayBuffer);

              // Import pdfjs dynamically
              const pdfjsLib = await import("pdfjs-dist");
              if (typeof window !== "undefined" && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
                pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;
              }

              const loadingTask = pdfjsLib.getDocument({ data: pdfData });
              const pdf = await loadingTask.promise;
              const page = await pdf.getPage(1);
              const viewport = page.getViewport({ scale: 2 });

              const canvas = document.createElement("canvas");
              const context = canvas.getContext("2d")!;
              canvas.width = viewport.width;
              canvas.height = viewport.height;

              await page.render({ canvasContext: context, viewport }).promise;
              ecgData = canvas.toDataURL("image/png");
              console.log("PDF converted to image using pdfBlob, length:", ecgData.length);
            } catch (error) {
              console.error("Error converting PDF blob to image:", error);
            }
          }

          // If conversion failed or pdfBlob not available, try using ecgData
          if (!ecgData && ecgData) {
            try {
              // ecgData is PDF base64, convert it to image
              const base64String = String(ecgData).includes('base64,') ?
              String(ecgData).split('base64,')[1] :
              String(ecgData);

              const byteCharacters = atob(base64String);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const pdfData = new Uint8Array(byteNumbers);

              const pdfjsLib = await import("pdfjs-dist");
              if (typeof window !== "undefined" && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
                pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;
              }

              const loadingTask = pdfjsLib.getDocument({ data: pdfData });
              const pdf = await loadingTask.promise;
              const page = await pdf.getPage(1);
              const viewport = page.getViewport({ scale: 2 });

              const canvas = document.createElement("canvas");
              const context = canvas.getContext("2d")!;
              canvas.width = viewport.width;
              canvas.height = viewport.height;

              await page.render({ canvasContext: context, viewport }).promise;
              ecgData = canvas.toDataURL("image/png");
              console.log("PDF converted to image using ecgData, length:", ecgData.length);
            } catch (error) {
              console.error("Error converting PDF from ecgData to image:", error);
            }
          }

          // Final fallback: fetch and convert
          if (!ecgData) {
            try {
              const base64EcgUrl = await getBase64FromUrl(ecgUrl);
              if (base64EcgUrl) {
                const base64String = String(base64EcgUrl).includes('base64,') ?
                String(base64EcgUrl).split('base64,')[1] :
                String(base64EcgUrl);

                const byteCharacters = atob(base64String);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const pdfData = new Uint8Array(byteNumbers);

                const pdfjsLib = await import("pdfjs-dist");
                if (typeof window !== "undefined" && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
                  pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`;
                }

                const loadingTask = pdfjsLib.getDocument({ data: pdfData });
                const pdf = await loadingTask.promise;
                const page = await pdf.getPage(1);
                const viewport = page.getViewport({ scale: 2 });

                const canvas = document.createElement("canvas");
                const context = canvas.getContext("2d")!;
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await page.render({ canvasContext: context, viewport }).promise;
                ecgData = canvas.toDataURL("image/png");
                console.log("PDF converted to image after fetch, length:", ecgData.length);
              }
            } catch (error) {
              console.error("Error fetching and converting PDF:", error);
            }
          }
        } else {
          // Regular image URL - use ecgData if available, otherwise fetch
          if (ecgData) {
            const base64String = String(ecgData).includes('base64,') ?
            String(ecgData).split('base64,')[1] :
            String(ecgData);
            ecgData = `data:image/png;base64,${base64String}`;
            console.log("Using ecgData for image, length:", ecgData.length);
          } else {
            const base64EcgUrl = await getBase64FromUrl(ecgUrl);
            ecgData = base64EcgUrl ?
            `data:image/png;base64,${String(base64EcgUrl).split("base64,")[1]}` :
            "";
            console.log("Fetched image ECG, length:", ecgData.length);
          }
        }
      }

      console.log("ECG data length:", ecgData?.length || 0);
      console.log("ECG data preview:", ecgData?.substring(0, 100) || "empty");
      console.log("ECG data starts with data:", ecgData?.startsWith("data:") || false);

      const imageData = base64ImageUrl ?
      `data:image/png;base64,${
      String(base64ImageUrl).split("base64,")[1]}` :

      "";

      // Only pass ecg_data if it's valid image data
      const validEcgData = ecgData && ecgData.startsWith("data:image") ? ecgData : "";

      console.log("Passing ECG data to PDF export:", validEcgData ? "YES" : "NO");

      await export_report_to_PDF(
        record,
        orgName,
        orgShortName,
        imageData,
        "",
        subjectType,
        ecgData || "" // Only pass if it's valid image data
      );
    } catch (error) {
      console.error("Error converting image or exporting report:", error);
      toast.error("Error exporting report. Please try again.");
    }
  };

  useEffect(() => {
    if (record?.customTestList && record?.customTestList?.length > 0) {
      setTypes((prevTypes) =>
      Array.from(new Set([...prevTypes, "other_test"]))
      );
      let values = Object.values(
        record?.customTestList?.reduce(
          (groups, test) => {
            const group = test?.customTest?.customTestGroup;

            if (group && group.id) {
              if (!groups[group.id]) {
                groups[group.id] = {
                  id: group.id,
                  name: group.name || "",
                  questions: []
                };
              }
              if (test?.value) {
                groups[group.id].questions.push({
                  name: test.customTest?.name || "",
                  value: test.value || "",
                  type: test.customTest?.type || ""
                });
              }
            }
            return groups;
          },
          {} as Record<
            string,
            {
              id: string;
              name: string;
              questions: {
                name: string;
                value: string;
                type: string;
              }[];
            }>

        )
      );
      setCustomTestList(values);
    }
  }, [record]);

  const getOrganizationImage = async () => {
    if (
    subject?.organization?.logoUrl ||
    record?.subject?.organization?.logoUrl)

    try {
      await imageUrlToBase64(
        "https://cors-anywhere.herokuapp.com/" +
        myOrgResult?.rootOrganization?.logoUrl || ""
      ).
      then((base64String: string) => {
        setOrganizationImage(base64String);
      }).
      catch((err: any) => {
        console.error("Conversion failed:", err);
      });
    } catch (ex) {}
  };
  useEffect(() => {
    getOrganizationImage();
  }, [subject, record]);
  useEffect(() => {
    setTypes(["reading"]);
    setCertificatePage("reading");
  }, []);

  useEffect(() => {
    if (record?.ecgReading?.ecgUrl || record?.ecgReading?.pdfUrl) {
      setTypes((prevTypes) => Array.from(new Set([...prevTypes, "ecg"])));
    }
  }, [record]);

  // Fetch PDF as blob to avoid CORS issues
  useEffect(() => {
    const fetchPdf = async () => {
      if (record?.ecgReading?.pdfUrl) {
        setPdfLoading(true);
        setPdfError(null);
        try {
          // Try fetching through the API first (like getBase64FromUrl does)
          const base64Data = await getBase64FromUrl(record.ecgReading.pdfUrl);
          if (base64Data) {
            setEcgData(base64Data);
            // Convert base64 to blob
            const base64String = String(base64Data).includes('base64,') ?
            String(base64Data).split('base64,')[1] :
            String(base64Data);
            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            setPdfBlob(blob);
            setPdfLoading(false);
          }
        } catch (error) {
          console.error("Error fetching PDF through API, trying direct fetch:", error);
          // Fallback to direct fetch
          try {
            const response = await axios.get(record.ecgReading.pdfUrl, {
              responseType: 'blob'
            });
            setPdfBlob(response.data);
            setPdfLoading(false);
          } catch (fetchError) {
            console.error("Error fetching PDF directly:", fetchError);
            setPdfError("Failed to load PDF. CORS or network error.");
            // Fallback to URL as string
            setPdfBlob(record.ecgReading.pdfUrl);
            setPdfLoading(false);
          }
        }
      }
    };

    if (visible && record?.ecgReading?.pdfUrl) {
      fetchPdf();
    } else {
      setPdfBlob(null);
      setPdfLoading(false);
    }
  }, [visible, record?.ecgReading?.pdfUrl]);

  const handleNext = () => {
    const currentIndex = types.findIndex((type) => type === certificatePage);
    const nextIndex = (currentIndex + 1) % types.length;
    setCertificatePage(types[nextIndex] as "reading" | "ecg" | "other_test");
  };

  const handlePrevious = () => {
    const currentIndex = types.findIndex((type) => type === certificatePage);
    const previousIndex = (currentIndex - 1 + types.length) % types.length;
    setCertificatePage(
      types[previousIndex] as "reading" | "other_test" | "ecg"
    );
  };

  const handleCancel = () => {
    onCancel();
    setTypes(["reading"]);
    setCertificatePage("reading");
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleCancel} // Correct prop name
      footer={null}
      closeIcon={<CloseOutlined />}
      width={500}
      title={
      <span className="health-screening-report-modal-header">
          {`${myOrgResult?.rootOrganization?.name}  `}
          {t("health_screening_report")}
        </span>}>

      
      <div className="health-screening-report">
        <hr style={{ color: "gray" }}></hr>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <p style={{ display: "flex" }}>
              <strong style={{ width: "45px" }}>Name:</strong>
              <span> {record?.subject?.name || subject?.name}</span>
            </p>
            <p style={{ display: "flex" }}>
              <strong style={{ width: "45px" }}>Gender:</strong>
              {startCase(record?.subject?.gender || subject?.gender)}
            </p>
            <p style={{ display: "flex" }}>
              <strong style={{ width: "45px" }}>Date:</strong>
              <span>
                {record?.datetime &&
                moment(new Date(record?.datetime)).format(
                  "DD-MMM-YYYY hh:mm A"
                )}
              </span>
            </p>
          </div>
          <div>
            {record?.subject?.keyValueList?.[0]?.organizationSubjectKey?.
            name &&
            <p style={{ display: "flex" }}>
                <strong
                style={{ width: "45px" }}>
                {`${record?.subject?.keyValueList?.[0]?.organizationSubjectKey?.name}`}</strong>{" "}
                {record?.subject?.keyValueList?.[0]?.value}
              </p>}
            
            <p style={{ display: "flex" }}>
              <strong style={{ width: "45px" }}>Age:</strong>{" "}
              {record?.subject?.dob &&
              getAgeCalculate(record?.subject?.dob?.toString())}
            </p>
            <p>
              @ {record?.subject?.organization?.name}{" "}
              {record?.subject?.organization?.level?.name}
            </p>
          </div>
          {myOrgResult?.rootOrganization?.logoUrl ?
          <div>
              <img
            // style={{
            //   background: `url(${i})`,
            // }}
            src={myOrgResult?.rootOrganization?.logoUrl}
            className="heart-image"
            alt="Heart" />
            
            </div> :

          ""}
          
        </div>

        <hr style={{ color: "gray" }}></hr>
        {certificatePage === "reading" &&
        <table className="results-table">
            <thead>
              <tr>
                <th>Test Name</th>
                <th>Result</th>
                <th>Reference Range</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Pulse</td>
                <td>{record?.vincenseReading?.pulseValue || "-"}</td>
                <td>
                  {record?.vincenseReading?.pulseReferenceRange || "-"}
                </td>
              </tr>
              <tr>
                <td>Skin Temperature</td>
                <td>{record?.vincenseReading?.skinValue}</td>
                <td></td>
              </tr>
              <tr>
                <td>SpO2</td>
                <td>{record?.vincenseReading?.o2Value}</td>
                <td>{record?.vincenseReading?.o2ReferenceRange}</td>
              </tr>
              <tr>
                <td>Respiratory Rate</td>
                <td>{record?.vincenseReading?.rrValue || "-"}</td>
                <td>{record?.vincenseReading?.rrReferenceRange || "-"}</td>
              </tr>
              <tr>
                <td>Systolic</td>
                <td>{record?.bpReading?.systolicValue}</td>
                <td>{record?.bpReading?.systolicReferenceRange}</td>
              </tr>
              <tr>
                <td>Diastolic</td>
                <td>{record?.bpReading?.diastolicValue}</td>
                <td>{record?.bpReading?.diastolicReferenceRange}</td>
              </tr>
              <tr>
                <td>Mean</td>
                <td>
                  {record?.bpReading?.meanValue ?
                Number(record?.bpReading?.meanValue).toFixed(2) :
                "-"}
                </td>
                <td>{record?.bpReading?.meanReferenceRange}</td>
              </tr>
              <tr>
                <td>Sugar</td>
                <td>{record?.glucometerReading?.randomValue}</td>
                <td>{record?.glucometerReading?.randomReferenceRange}</td>
              </tr>
              <tr>
                <td>Weight</td>
                <td>{record?.weighingReading?.weightValue}</td>
                <td>{"-"}</td>
              </tr>
              <tr>
                <td>Height</td>
                <td>{record?.weighingReading?.heightValue}</td>
                <td>{"-"}</td>
              </tr>
              <tr>
                <td>BMI</td>
                <td>{record?.weighingReading?.bmiValue}</td>
                <td>{record?.weighingReading?.bmiReferenceRange}</td>
              </tr>
            </tbody>
          </table>}
        
        {certificatePage === "other_test" &&
        <>
            <Row align={"middle"} justify={"center"}>
              <Col>
                <u>
                  <h3>{"Other Tests"}</h3>
                </u>
              </Col>
            </Row>
            <CustomTestDetails items={customTestList} />
          </>}
        

        {certificatePage === "ecg" &&
        <>
            <Row align={"middle"} justify={"center"}>
              <Col>
                <u>
                  <h3>{"ECG Report"}</h3>
                </u>
              </Col>
            </Row>
            {record?.ecgReading?.pdfUrl ?
          // PDF Viewer for PDF URLs
          <div style={{ display: 'flex', justifyContent: 'center', overflow: 'auto', height: '300px' }}>
                {pdfLoading && !pdfBlob ?
            <div style={{ padding: '20px', textAlign: 'center' }}>Loading PDF...</div> :

            <Document
              file={pdfBlob || record.ecgReading.pdfUrl}
              loading={<div>Loading PDF...</div>}
              error={
              <div style={{ padding: '20px', textAlign: 'center' }}>
                      <div>Error loading PDF</div>
                      {pdfError &&
                <div style={{ fontSize: '12px', color: 'red', marginTop: '10px' }}>
                          {pdfError}
                        </div>}
                
                      <div style={{ fontSize: '11px', color: 'gray', marginTop: '5px' }}>
                        Check browser console for details
                      </div>
                    </div>}

              onLoadSuccess={() => {
                setPdfError(null);
                console.log("PDF loaded successfully");
              }}
              onLoadError={(error) => {
                console.error("PDF load error:", error);
                const errorMessage = error?.message || error?.toString() || "Failed to load PDF";
                setPdfError(errorMessage);
                console.error("Full error object:", error);
              }}
              options={{
                cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
                cMapPacked: true,
                standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standardFonts/`
              }}>
              
                    <Page
                pageNumber={1}
                width={400}
                renderTextLayer={true}
                renderAnnotationLayer={true} />
              
                  </Document>}
            
              </div> :

          // Image viewer for PNG URLs (existing Samtech format)
          <img src={record?.ecgReading?.ecgUrl} width={"400px"} alt="ECG" />}
          
          </>}
        

        <div className="modal-footer">
          <span>Powered by MedIoTek Health Systems Pvt Ltd</span>
          <img src={HEADER_LOGO} alt="VinCense" />
          <IoMdDownload
            size={25}
            color={"#8a164c"}
            onClick={handleClick}
            className="reading-certificate-icon" />
          
        </div>
      </div>

      {((record?.customTestList?.length ?? 0) > 0 ||
      record?.ecgReading?.ecgUrl ||
      record?.ecgReading?.pdfUrl) &&
      types?.length > 1 &&
      <div className="health-reading-report-modal_footer">
            <Button
          onClick={handlePrevious}
          disabled={certificatePage === "reading"}
          danger>
          
              Previous
            </Button>
            <Button
          onClick={handleNext}
          disabled={types[types?.length - 1] === certificatePage}
          type="primary">
          
              Next
            </Button>
          </div>}
      
    </Modal>);

};

export default HealthScreeningReportModal;